/**
 * TimeoutRenderer - Minimal inline input for timeout duration
 *
 * Clean design: label on left, input + hint on right
 */

import { useState, useCallback } from 'react';
import { makeStyles, tokens, Text, Input, Field } from '@coreai/fluentui-react';
import type { SettingRendererProps } from './registry';
import { registerSettingRenderer } from './registry';
import { validateDuration } from '@/utils/validation';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalM,
    minHeight: '44px',
  },
  label: {
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    paddingTop: tokens.spacingVerticalXS, // Align with input
  },
  inputWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: tokens.spacingVerticalXXS,
  },
  input: {
    width: '120px',
  },
  hint: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground4,
  },
});

export interface TimeoutRendererProps extends SettingRendererProps<string> {
  label?: string;
}

/**
 * TimeoutRenderer component for duration input
 */
export const TimeoutRenderer = ({
  setting,
  onChange,
  readOnly = false,
  label = 'Timeout',
}: TimeoutRendererProps) => {
  const styles = useStyles();
  const [validationError, setValidationError] = useState<string | null>(null);

  const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;

    // Validate on change
    const validation = validateDuration(value);
    setValidationError(validation.error ?? null);

    // Always update the value (let user type freely)
    onChange(value || undefined);
  }, [onChange]);

  const handleBlur = useCallback(() => {
    // Re-validate on blur to show final error state
    const validation = validateDuration(setting.value);
    setValidationError(validation.error ?? null);
  }, [setting.value]);

  // Don't render if setting is not supported
  if (!setting.isSupported) {
    return null;
  }

  return (
    <div className={styles.container}>
      <Text className={styles.label}>{label}</Text>
      <div className={styles.inputWrapper}>
        <Field
          {...(validationError ? { validationState: 'error' as const, validationMessage: validationError } : {})}
        >
          <Input
            className={styles.input}
            value={setting.value ?? ''}
            onChange={handleChange}
            onBlur={handleBlur}
            placeholder="PT1H"
            disabled={readOnly}
            aria-label={label}
          />
        </Field>
        <Text className={styles.hint}>e.g., PT1H</Text>
      </div>
    </div>
  );
};

TimeoutRenderer.displayName = 'TimeoutRenderer';

// Register this renderer
registerSettingRenderer('timeout', TimeoutRenderer);
